import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const COMMODITIES = ["CPO", "RBD Palm Olein", "Crude Soybean Oil", "Soybeans", "Sunflower Oil"];
const TERMS = ["FOB", "CFR", "CIF", "DES", "DAP"];
const PAY = ["LC at sight", "LC usance", "DP", "DA", "Open account"];
const PRICING = ["Fixed", "Floating", "TBF"];
const MONTHS = ["Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26"];

const empty = (side) => ({
  contract_ref: "", trade_no: "", trade_type: "Forward", commodity: "CPO", grade: "Standard export grade",
  origin: "", counterparty_id: "", quantity: "", tolerance_pct: 5, uom: "MT", pricing_basis: "Fixed",
  futures_ref: "-", differential: 0, price: "", currency: "USD", delivery_term: side === "purchase" ? "FOB" : "CFR",
  load_port: "", discharge_port: "", payment_term: "LC at sight", incoterm: side === "purchase" ? "FOB" : "CFR",
  delivery_month: "Aug-26", quality_terms: "SGS inspection; FOSFA arbitration", status: "Draft", version: 1,
  destination_market: "", lc_required: true,
});

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-xs font-bold uppercase tracking-wider text-orange-600 dark:text-orange-400 border-b border-slate-200 dark:border-slate-800 pb-1">{title}</p>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{children}</div>
  </div>
);

export default function ContractForm({ side, open, onClose, initial }) {
  const qc = useQueryClient();
  const [f, setF] = useState(empty(side));
  const { data: cps = [] } = useQuery({ queryKey: ["counterparties"], queryFn: () => api.get("/counterparties") });
  const { data: entities = [] } = useQuery({ queryKey: ["entities"], queryFn: () => api.get("/entities") });

  useEffect(() => {
    if (open) setF(initial ? { ...empty(side), ...initial } : empty(side));
  }, [open, initial, side]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const cpList = cps.filter((c) => (side === "purchase" ? c.type === "Supplier" : c.type === "Buyer"));

  const save = async () => {
    if (!f.contract_ref || !f.quantity || !f.price || !f.counterparty_id) {
      return toast.error("Fill contract ref, counterparty, quantity and price");
    }
    const cp = cps.find((c) => c.id === f.counterparty_id);
    const payload = {
      ...f, quantity: Number(f.quantity), price: Number(f.price),
      tolerance_pct: Number(f.tolerance_pct), differential: Number(f.differential),
      counterparty_name: cp?.name, entity_id: entities.find((e) => e.is_primary)?.id,
      vessel_ids: initial?.vessel_ids || [],
    };
    const path = side === "purchase" ? "purchase-contracts" : "sale-contracts";
    if (initial?.id) await api.put(`/${path}/${initial.id}`, payload);
    else await api.post(`/${path}`, payload);
    toast.success(`${side === "purchase" ? "Purchase" : "Sale"} contract saved`);
    qc.invalidateQueries();
    onClose();
  };

  const value = (Number(f.quantity) || 0) * (Number(f.price) || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid={`${side}-contract-form`}>
        <DialogHeader>
          <DialogTitle>{initial ? "Edit" : "New"} {side === "purchase" ? "Purchase" : "Sale"} Contract</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <Section title="Contract Identification">
            <Field label="Contract Reference *"><Input data-testid="cf-ref" value={f.contract_ref} onChange={(e) => set("contract_ref", e.target.value)} placeholder="AGP-2026-09" /></Field>
            <Field label="Trade No."><Input data-testid="cf-tradeno" value={f.trade_no} onChange={(e) => set("trade_no", e.target.value)} /></Field>
            <Field label="Trade Type">
              <Select value={f.trade_type} onValueChange={(v) => set("trade_type", v)}>
                <SelectTrigger data-testid="cf-tradetype"><SelectValue /></SelectTrigger>
                <SelectContent>{["Spot", "Forward", "Term"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Commodity & Specification">
            <Field label="Commodity">
              <Select value={f.commodity} onValueChange={(v) => set("commodity", v)}>
                <SelectTrigger data-testid="cf-commodity"><SelectValue /></SelectTrigger>
                <SelectContent>{COMMODITIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Grade / Specification"><Input data-testid="cf-grade" value={f.grade} onChange={(e) => set("grade", e.target.value)} /></Field>
            <Field label="Origin (Country/Port)"><Input data-testid="cf-origin" value={f.origin} onChange={(e) => set("origin", e.target.value)} placeholder="Malaysia/Belawan" /></Field>
          </Section>

          <Section title={side === "purchase" ? "Supplier & Volume" : "Buyer & Volume"}>
            <Field label={side === "purchase" ? "Supplier *" : "Buyer *"}>
              <Select value={f.counterparty_id} onValueChange={(v) => set("counterparty_id", v)}>
                <SelectTrigger data-testid="cf-counterparty"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cpList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Quantity (MT) *"><Input data-testid="cf-qty" type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
            <Field label="Tolerance %"><Input data-testid="cf-tol" type="number" value={f.tolerance_pct} onChange={(e) => set("tolerance_pct", e.target.value)} /></Field>
          </Section>

          <Section title="Pricing">
            <Field label="Pricing Basis">
              <Select value={f.pricing_basis} onValueChange={(v) => set("pricing_basis", v)}>
                <SelectTrigger data-testid="cf-pricing"><SelectValue /></SelectTrigger>
                <SelectContent>{PRICING.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Futures Reference"><Input data-testid="cf-futref" value={f.futures_ref} onChange={(e) => set("futures_ref", e.target.value)} /></Field>
            <Field label="Differential ($/MT)"><Input data-testid="cf-diff" type="number" value={f.differential} onChange={(e) => set("differential", e.target.value)} /></Field>
            <Field label="Price ($/MT) *"><Input data-testid="cf-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} /></Field>
            <Field label="Currency">
              <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="cf-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{["USD", "SGD", "INR"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Contract Value"><Input readOnly value={value ? `$${value.toLocaleString()}` : "-"} className="font-mono bg-slate-50 dark:bg-slate-800" /></Field>
          </Section>

          <Section title="Delivery & Logistics">
            <Field label="Delivery Term">
              <Select value={f.delivery_term} onValueChange={(v) => { set("delivery_term", v); set("incoterm", v); }}>
                <SelectTrigger data-testid="cf-delterm"><SelectValue /></SelectTrigger>
                <SelectContent>{TERMS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Load Port"><Input data-testid="cf-loadport" value={f.load_port} onChange={(e) => set("load_port", e.target.value)} /></Field>
            <Field label="Discharge Port"><Input data-testid="cf-dischport" value={f.discharge_port} onChange={(e) => set("discharge_port", e.target.value)} /></Field>
            <Field label="Delivery Month">
              <Select value={f.delivery_month} onValueChange={(v) => set("delivery_month", v)}>
                <SelectTrigger data-testid="cf-delmonth"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {side === "sale" && <Field label="Destination Market"><Input data-testid="cf-dest" value={f.destination_market} onChange={(e) => set("destination_market", e.target.value)} /></Field>}
          </Section>

          <Section title="Payment & Terms">
            <Field label="Payment Term">
              <Select value={f.payment_term} onValueChange={(v) => set("payment_term", v)}>
                <SelectTrigger data-testid="cf-payterm"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cf-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["Draft", "Confirmed", "Fully Executed", "Closed", "Cancelled"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="col-span-2 md:col-span-3">
              <Field label="Quality / Inspection Terms"><Textarea data-testid="cf-quality" value={f.quality_terms} onChange={(e) => set("quality_terms", e.target.value)} rows={2} /></Field>
            </div>
          </Section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="cf-save" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Save Contract</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
